import api, { API_PATHS } from "@/api/index";
import memeVotingContract from "@/contracts/memeVoting";
import memeAuctionContract from "@/contracts/memeAuction";
import generalClient from "@/api/general";

// Confirmed against the old backend's own source: this was a static admin-curated blocklist (an
// env var, not derived from anything on-chain), so it's just carried over as a constant.
const RESTRICTED_MEMES = [37, 39];

class MemeClient {
  // memeCreate/memeVote genuinely need the backend: the contract requires a signature proving the
  // server-side captcha/throttle check passed, and only the old backend's private key can produce
  // one the contract accepts. There is no on-chain substitute for that without a contract change.
  async memeCreate(
    file: File,
    captchaResult: any
  ): Promise<{
    url: string;
    signature: string;
  }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("captcha", captchaResult);

    try {
      const response = await api.post(API_PATHS.MEME_CREATE, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      return null;
    }
  }

  async memeVote(
    votes: number[],
    captcha: any
  ): Promise<{
    signature: string;
  }> {
    try {
      const response = await api.post(API_PATHS.MEME_VOTE, {
        captcha,
        votes,
      });

      return response.data;
    } catch (error) {
      return null;
    }
  }

  // Everything below turned out to be plain on-chain queries against the meme voting / meme
  // auction contracts (confirmed against the old backend's own source) - reading directly from
  // chain instead of the now-dead REST endpoints.
  async memeVotesAll(nonce: number): Promise<{
    votes: number;
    allVotes: number;
  } | null> {
    try {
      const period = await generalClient.currentPeriod();

      if (null === period) {
        return null;
      }

      return await memeVotingContract.memeVotesAll(nonce, period);
    } catch (e) {
      return null;
    }
  }

  async memesVotesAll(nonces: number[]): Promise<{
    [nonce: string]: {
      votes: number;
      allVotes: number;
    };
  }> {
    if (!nonces.length) {
      return {};
    }

    try {
      const period = await generalClient.currentPeriod();

      if (null === period) {
        return {};
      }

      const results = await Promise.all(nonces.map((nonce) => memeVotingContract.memeVotesAll(nonce, period)));

      return nonces.reduce((acc, nonce, index) => {
        acc[nonce] = results[index];

        return acc;
      }, {});
    } catch (e) {
      return {};
    }
  }

  async memeVotes(nonce: number, period: number): Promise<number> {
    try {
      return await memeVotingContract.memeVotes(nonce, period);
    } catch (e) {
      return 0;
    }
  }

  async memesVotes(nonces: number[], period: number): Promise<{ [nonce: string]: number }> {
    if (!nonces.length) {
      return {};
    }

    try {
      const results = await Promise.all(nonces.map((nonce) => memeVotingContract.memeVotes(nonce, period)));

      return nonces.reduce((acc, nonce, index) => {
        acc[nonce] = results[index];

        return acc;
      }, {});
    } catch (e) {
      return {};
    }
  }

  async memeRarity(nonce: number): Promise<number> {
    try {
      return await memeAuctionContract.memeRarity(nonce);
    } catch (e) {
      return 0;
    }
  }

  async memesRarity(nonces: number[]): Promise<{ [nonce: number]: number }> {
    if (!nonces.length) {
      return {};
    }

    try {
      const results = await Promise.all(nonces.map((nonce) => memeAuctionContract.memeRarity(nonce)));

      return nonces.reduce((acc, nonce, index) => {
        acc[nonce] = results[index];

        return acc;
      }, {});
    } catch (e) {
      return {};
    }
  }

  async memeTopMeme(nonce: number): Promise<number | null> {
    try {
      const topNonce = await memeAuctionContract.memeToTopMeme(nonce);

      return topNonce || null;
    } catch (e) {
      return null;
    }
  }

  async memesTopMeme(nonces: number[]): Promise<{ [nonce: number]: number }> {
    if (!nonces.length) {
      return {};
    }

    try {
      const results = await Promise.all(nonces.map((nonce) => memeAuctionContract.memeToTopMeme(nonce)));

      return nonces.reduce((acc, nonce, index) => {
        if (results[index]) {
          acc[nonce] = results[index];
        }

        return acc;
      }, {});
    } catch (e) {
      return {};
    }
  }

  async memesRestricted(): Promise<number[]> {
    return RESTRICTED_MEMES;
  }
}

const memeClient = new MemeClient();

export default memeClient;
